import React, { useRef, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Bell, Search, User, LogOut } from 'lucide-react';

const Navbar = ({ user, onLogout }) => {
  const location  = useLocation();
  const navigate  = useNavigate();
  const navRef    = useRef(null);

  const [cachedUser, setCachedUser] = useState(user);
  useEffect(() => {
    if (user)          setCachedUser(user);
    if (user === null) setCachedUser(null);
  }, [user]);
  const activeUser = cachedUser;

  const navLinks = [
    { to: '/',            label: 'ALL' },
    { to: '/education',   label: 'EDUCATION' },
    { to: '/management',  label: 'MANAGEMENT' },
    { to: '/coming-soon', label: 'E-COMMERCE' },
    { to: '/coming-soon', label: 'MOVIES' },
    { to: '/coming-soon', label: 'PODCAST' },
    { to: '/coming-soon', label: 'MUSIC' },
    { to: '/games',       label: 'GAMES' },
  ];

  const getDefaultIndex = () => {
    const idx = navLinks.findIndex(link => link.to === location.pathname);
    return idx >= 0 ? idx : 0;
  };

  const [activeIndex, setActiveIndex] = useState(getDefaultIndex);

  useEffect(() => {
    const idx = navLinks.findIndex(link => link.to === location.pathname);
    if (idx >= 0 && navLinks[idx].to !== '/coming-soon') {
      setActiveIndex(idx);
    }
  }, [location.pathname]);

  // ✅ NO own keydown handler here
  // Pehle yahan ek aur keydown listener tha jo useTVNavigation ke saath
  // double-fire karta tha → har press pe 2 steps jump hota tha navbar mein

  const displayName = activeUser?.name || activeUser?.email?.split('@')[0] || '';

  return (
    <nav
      ref={navRef}
      className="fixed top-0 w-full z-50 bg-gradient-to-b from-black/90 to-transparent"
      style={{ padding: 'clamp(6px,1.2vh,14px) clamp(10px,2vw,20px)' }}
    >
      <style>{`
        [data-tv-nav]:focus {
          outline: 2px solid #14b8a6 !important;
          outline-offset: 3px;
          border-radius: 6px;
        }
      `}</style>
      <div className="flex items-center justify-between gap-2" style={{ minWidth: 0 }}>

        {/* Logo */}
        <div className="flex items-center flex-shrink-0" style={{ gap: 'clamp(4px,0.8vw,10px)' }}>
          <div style={{ width:'clamp(28px,3.5vw,40px)', height:'clamp(28px,3.5vw,40px)', background:'#14b8a6', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <span style={{ color:'#fff', fontWeight:700, fontSize:'clamp(11px,1.4vw,16px)' }}>
              {activeUser ? displayName.charAt(0).toUpperCase() : 'A'}
            </span>
          </div>
          <span style={{ color:'#fff', fontSize:'clamp(11px,1.4vw,17px)', fontWeight:600, whiteSpace:'nowrap' }}>
            {activeUser ? displayName : 'Athena'}
          </span>
        </div>

        {/* Nav links */}
        <div
          className="flex items-center flex-1 justify-center"
          style={{ gap: '0px', minWidth: 0, flexShrink: 1 }}
        >
          {navLinks.map((link, i) => {
            const isActive = activeIndex === i;
            return (
              <Link
                key={i}
                to={link.to}
                data-tv-nav
                tabIndex={0}
                onClick={() => setActiveIndex(i)}
                className="text-white font-semibold whitespace-nowrap outline-none transition-all duration-150"
                style={{
                  padding: 'clamp(4px,0.8vh,8px) clamp(5px,0.8vw,10px)',
                  fontSize: 'clamp(9px,1.1vw,13px)',
                  letterSpacing: '0.02em',
                  borderBottom: isActive ? '2px solid white' : '2px solid transparent',
                  opacity: isActive ? 1 : 0.75,
                  display: 'inline-block',
                  flexShrink: 0,
                }}
              >
                {link.label}
              </Link>
            );
          })}
        </div>

        {/* Right icons */}
        <div className="flex items-center flex-shrink-0" style={{ gap: 'clamp(3px,0.6vw,8px)' }}>
          <button data-tv-nav tabIndex={0}
            className="text-white rounded-full hover:bg-white/20 outline-none transition"
            style={{ padding: 'clamp(4px,0.6vw,8px)' }}>
            <Bell style={{ width:'clamp(14px,1.8vw,22px)', height:'clamp(14px,1.8vw,22px)' }} />
          </button>
          <button data-tv-nav tabIndex={0}
            className="text-white rounded-full hover:bg-white/20 outline-none transition"
            style={{ padding: 'clamp(4px,0.6vw,8px)' }}>
            <Search style={{ width:'clamp(14px,1.8vw,22px)', height:'clamp(14px,1.8vw,22px)' }} />
          </button>

          {activeUser ? (
            <button
              data-tv-nav tabIndex={0}
              onClick={onLogout}
              className="flex items-center text-white rounded-full border border-red-500/60 hover:bg-red-500/20 outline-none transition whitespace-nowrap"
              style={{ fontSize:'clamp(9px,1vw,12px)', padding:'clamp(4px,0.6vh,8px) clamp(8px,1vw,14px)', gap:'clamp(3px,0.4vw,6px)' }}
            >
              <LogOut style={{ width:'clamp(12px,1.4vw,18px)', height:'clamp(12px,1.4vw,18px)', color:'#f87171' }} />
              <span style={{ color:'#f87171' }}>LOGOUT</span>
            </button>
          ) : (
            <button
              data-tv-nav tabIndex={0}
              onClick={() => navigate('/login')}
              className="flex items-center text-white rounded-full border border-white/50 hover:bg-white/20 outline-none transition whitespace-nowrap"
              style={{ fontSize:'clamp(9px,1vw,12px)', padding:'clamp(4px,0.6vh,8px) clamp(8px,1vw,14px)', gap:'clamp(3px,0.4vw,6px)' }}
            >
              <User style={{ width:'clamp(12px,1.4vw,18px)', height:'clamp(12px,1.4vw,18px)' }} />
              <span>LOGIN</span>
            </button>
          )}

          <button
            data-tv-nav tabIndex={0}
            className="bg-yellow-500 text-black font-bold rounded-md hover:bg-yellow-400 outline-none transition whitespace-nowrap"
            style={{ fontSize:'clamp(9px,1vw,12px)', padding:'clamp(4px,0.6vh,8px) clamp(8px,1.2vw,16px)' }}
          >
            PREMIUM
          </button>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;